"use client";
import { TooltipProvider } from "@cmp-src/primitives/tooltip";
import { ISchedule } from "@gettactic/api";
import { isWorkplaceRequestsEnabled } from "@gettactic/helpers/src/featureFlags";
import { useOrgFeatureFlag } from "@gettactic/hooks";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { WPRequests } from "@/features/workplace-requests/components/WPRequests";
import { reservationWhenFromReserveForm } from "@/features/workplace-requests/lib/reservationWhen";
import type { NewRequestResourceContext } from "@/features/workplace-requests/views/queue/NewRequestWizard";
import {
  WorkplaceRequestParams,
  WorkplaceRequestsRef
} from "@/types/workplace-requests";

interface ReserveEditFormWPRequestsProps {
  schedule: ISchedule;
  officeId: string;
  form: UseFormReturn<SaveReservationData>;
}

export const ReserveEditFormWPRequests = forwardRef<
  WorkplaceRequestsRef,
  ReserveEditFormWPRequestsProps
>(({ schedule, officeId, form }, ref) => {
  const wpRequestsRef = useRef<WorkplaceRequestsRef>(undefined);
  const workplaceRequestsFeatureFlag = useOrgFeatureFlag("workplace_requests");
  const shouldShow = isWorkplaceRequestsEnabled(workplaceRequestsFeatureFlag);

  // Expose the submit function to the parent component
  useImperativeHandle(
    ref,
    () => ({
      submitWorkplaceRequests: (params: WorkplaceRequestParams) => {
        if (wpRequestsRef.current?.submitWorkplaceRequests) {
          return wpRequestsRef.current.submitWorkplaceRequests(params);
        }
        return Promise.resolve({ success: true });
      },
      validateAndGetRequests: () => {
        if (wpRequestsRef.current?.validateAndGetRequests) {
          return wpRequestsRef.current.validateAndGetRequests();
        }
        return Promise.resolve(null);
      }
    }),
    []
  );

  const recurringValue = form.watch("recurring");
  const isRecurring =
    recurringValue && recurringValue !== "" && recurringValue !== "no";

  if (isRecurring || !shouldShow) {
    return null;
  }

  // Pre-fill the request wizard's location from the reservation's resource,
  // mirroring how filing a request from a map resource works.
  const resourceContext: NewRequestResourceContext | undefined =
    schedule.resource
      ? {
          id: schedule.resource.id,
          name: schedule.resource.label || schedule.resource.name,
          officeId: schedule.resource.office_id || officeId,
          type: schedule.resource.type
        }
      : undefined;

  return (
    <div className="pt-4" onClick={(e) => e.stopPropagation()}>
      <TooltipProvider>
        <WPRequests
          reservationId={schedule.id}
          officeId={officeId}
          onSubmitRef={wpRequestsRef}
          className="border-neutral-200 border-t pt-4"
          resource={resourceContext}
          reservationWhen={
            reservationWhenFromReserveForm({
              weekDates: form.watch("weekDates"),
              time: form.watch("time"),
              duration: form.watch("duration")
            }) ?? undefined
          }
        />
      </TooltipProvider>
    </div>
  );
});

ReserveEditFormWPRequests.displayName = "ReserveEditFormWPRequests";
